/**
 * MiniDayPicker Component
 */

import React, { FC, useState, useEffect } from 'react';
import { withTheme } from 'styled-components';
import { MiniDayPickerProps } from './miniDayPicker.types';
import { buildDateSlide } from '../DayPicker/dayPicker';
import makeSafeDate from '../../utils/makeSafeDate';
import { Wrapper } from './miniDayPicker.style';

import add from 'date-fns/add';
import sub from 'date-fns/sub';
import format from 'date-fns/format';

const standFormat: string = 'M-dd-yyyy';

const MiniDayPicker: FC<MiniDayPickerProps> = ({
  centeredDate = new Date(),
  selectedDate,
  theme = {},
  locale = 'en',
  todayLabel = '',
  returnedDateFormat,
  handlers = {},
  maxDate,
  minDate,
  isLoading,
  width = '100%',
  hiddenDates = [],
  className,
}: MiniDayPickerProps) => {
  const { dark = false } = theme;

  const dateCentered = makeSafeDate(centeredDate);

  // Set the selected date and, if there is none, it'll be the centered one
  const [currentSelectedDate, setSelectedDate] = useState<Date>(selectedDate ? makeSafeDate(selectedDate) : dateCentered);

  // Set up an object to look up which dates are to be hidden
  const hiddenDatesMap: object = {};
  for (let i: number = 0; i < hiddenDates.length; i++) {
    hiddenDatesMap[format(makeSafeDate(hiddenDates[i]), standFormat)] = true;
  }

  // Set up the date array
  // Check with the hidden dates before adding
  const dateArr: Date[] = [];
  const startDate: Date = sub(dateCentered, {days: 1});
  for (let i: number = 0; i < 3; i++) {
    const date: Date = add(startDate, {days: i});
    if (!hiddenDatesMap[format(date, standFormat)]) {
      dateArr.push(date);
    }
  }

  // Establish today's date
  const today: Date = new Date();

  // Establish what the min and max dates are (if any)
  const minDateParam: Date | undefined = minDate ? makeSafeDate(minDate) : undefined;
  const maxDateParam: Date | undefined = maxDate ? makeSafeDate(maxDate) : undefined;

  useEffect(() => {
    if (selectedDate) {
      setSelectedDate(makeSafeDate(selectedDate));
    }
  }, [selectedDate]);

  return (
    <Wrapper
      width={ width }
    >
      {
        dateArr.map((date, i) => {
          // Utilize the "buildDateSlide" func exported from dayPicker
          return buildDateSlide(
            i,
            date,
            currentSelectedDate,
            today,
            () => {
              const dFormat: string = format(dateArr[i], standFormat)
              if (format(currentSelectedDate, standFormat) !== dFormat) {
                setSelectedDate(dateArr[i]);
                handlers.onClick && handlers.onClick(
                  returnedDateFormat ? format(dateArr[i], returnedDateFormat) : dateArr[i]
                )
              }
            },
            !!isLoading,
            dark,
            todayLabel,
            locale,
            1,
            '',
            true,
            minDateParam,
            maxDateParam,
            className
          )
        })
      }
    </Wrapper>
  );
};

export default withTheme(MiniDayPicker);
